import {useState, useEffect, useMemo} from 'react';

import moment from 'moment';

import {Button} from 'shells/button/button';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {Illustration} from 'shells/illustrations/illustration';
import {PaginatedTable} from 'shells/table/paginated-table';
import {convertUrlSortParamToTableSortBy} from 'shells/table/table-sort-helpers';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {FormSubmissionSortOrder} from 'nutshell-graphql-types';
import type {GetSubmissions_submissions_submissions_edges_node as Submission} from 'nutshell-graphql-types';

import {OpenableModals} from '../../../openable-modals/openable-modals';
import {useOpenableModal} from '../../../openable-modals/use-openable-modal';

import {useGetForm} from '../graphql/hooks/use-get-form';
import {useGetSubmissions, DEFAULT_PAGE_SIZE} from '../graphql/hooks/use-get-submissions';

import {DownloadSubmissionsButton} from './download-submissions-button';
import {getConditionalColumns, getCompletionPercentage} from './helpers';

interface Props {
    formId: string;
    urlParams: any;
    onParamChange: (arg1: any, arg2: boolean) => any;
}

export function FormSubmissions(props: Props) {
    const {urlParams, onParamChange} = props;

    const sort = urlParams.submissions ? urlParams.submissions.sort : null;

    const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
    const [defaultSort, setDefaultSort] = useState(sort);

    const {
        submissions,
        totalCount,
        fetchPreviousPage,
        fetchNextPage,
        fetchFirstPageWithNewSort,
        isLoading,
    } = useGetSubmissions({
        formId: props.formId,
        sort: defaultSort,
    });
    const {form} = useGetForm({formId: props.formId});

    const selectedSubmission: Submission | undefined =
        selectedSubmissionId && submissions
            ? submissions.find((submission) => submission.id === selectedSubmissionId)
            : undefined;

    const {onOpen: onOpenSubmissionModal} = useOpenableModal(OpenableModals.FORM_SUBMISSION_MODAL, {
        submissionId: selectedSubmissionId,
        primaryEntity: selectedSubmission?.primaryEntity,
        submissionTime: selectedSubmission?.submissionTime,
        form,
    });

    useEffect(() => {
        if (selectedSubmissionId) {
            onOpenSubmissionModal();
            setSelectedSubmissionId(null);
        }
    }, [selectedSubmissionId, onOpenSubmissionModal]);

    const columns = useMemo(() => {
        const conditionalColumns = form ? getConditionalColumns(form) : [];

        return [
            {
                id: 'submissionTime',
                Header: 'Submission time',
                accessor: (submission: Submission) => submission,
                dataType: 'component',
                Cell: ({cell}: any) => {
                    // Checking this because after 6 days, the RelativeTimestamp no longer shows the time, just the date.
                    // Since the column is titled "Submission time", we want to always show the time, so we use a FormattedTimestamp if it's older than 6 days.
                    const isSubmittedMoreThan6DaysAgo =
                        moment.unix(cell.value.submissionTime).diff(moment(), `days`, true) < -6;

                    return (
                        <Button
                            onClick={() => setSelectedSubmissionId(cell.value.id)}
                            variant='text-primary'
                            noPadding={true}
                        >
                            {isSubmittedMoreThan6DaysAgo ? (
                                <FormattedTimestamp
                                    datetime={cell.value.submissionTime}
                                    format={FormattedTimestamp.formats.shortDayMonthYearWithAtTime}
                                />
                            ) : (
                                <RelativeTimestamp
                                    datetime={cell.value.submissionTime}
                                    format='hourHybrid'
                                />
                            )}
                        </Button>
                    );
                },
                minWidth: 125,
                width: 175,
                maxWidth: 225,
                shouldTruncate: true,
            },
            {
                id: 'primaryEntity',
                Header: 'Respondent',
                accessor: (submission: Submission) => submission.primaryEntity || submission,
                dataType: 'linkedEntity',
                minWidth: 75,
                width: 125,
                maxWidth: 250,
                disableSortBy: true,
            },
            {
                id: 'completionPercentage',
                Header: 'Completion %',
                accessor: (submission: Submission) => getCompletionPercentage(submission),
                dataType: 'percentage',
                minWidth: 50,
                width: 105,
                maxWidth: 150,
                disableSortBy: true,
            },
            ...conditionalColumns,
        ];
    }, [form]);

    useEffect(() => {
        if (!sort && defaultSort) {
            // Necessary to keep sort state across page reloads
            // For some reason the second render does not have our sort param in urlParams
            // so we need to set it back
            onParamChange({submissions: {sort: defaultSort}}, true);
        }
        if (!sort && !defaultSort) {
            // If no sort is provided, default to SUBMITTED_TIME_DESC
            onParamChange({submissions: {sort: SortOrder.SUBMITTED_TIME_DESC}}, true);
            setDefaultSort(SortOrder.SUBMITTED_TIME_DESC);
        }
    }, [sort, onParamChange, defaultSort]);

    const updateUrlParamsOnSortChange = (newSortString: string) => {
        onParamChange({submissions: {sort: newSortString}}, true);
    };

    return (
        <PaginatedTable
            columns={columns}
            sortBy={sort ? convertUrlSortParamToTableSortBy(sort) : undefined}
            totalCount={totalCount}
            fetchNextPage={fetchNextPage}
            fetchPreviousPage={fetchPreviousPage}
            updateUrlParamsOnSortChange={updateUrlParamsOnSortChange}
            fetchFirstPageWithNewSort={(sortBy: string) =>
                fetchFirstPageWithNewSort(sortBy as FormSubmissionSortOrder)
            }
            pageSize={DEFAULT_PAGE_SIZE}
            buttons={
                totalCount > 0 && (
                    <DownloadSubmissionsButton formId={props.formId} totalCount={totalCount} />
                )
            }
            data={submissions ?? []}
            extendToFitWidth={true}
            isLoading={isLoading}
            emptyState={
                <EmptyStateSmall
                    icon={
                        <div style={{width: 80}}>
                            <Illustration src='cactus-empty-state' width='100%' height='100%' />
                        </div>
                    }
                    title='No submissions were found'
                    subtitle='Once users start submitting this form, you will see their submissions here.'
                />
            }
        />
    );
}

const SortOrder = {
    SUBMITTED_TIME_DESC: '-submissionTime',
    SUBMITTED_TIME_ASC: 'submissionTime',
} as const;
